import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { getVoiceChoice, setVoiceChoice, type VoiceChoice } from '../audio/voices';
import {
  CLIP_VOICES,
  clipVoiceLabel,
  defaultClipVoiceId,
  hasClipVoice,
  primeAudio,
} from '../audio/clipAnnouncer';

// Clip voices only (the natural, pre-recorded ones). "" = Automatic (default).
function parseValue(value: string): VoiceChoice {
  if (value.startsWith('clip:')) return { kind: 'clip', id: value.slice(5) };
  return { kind: 'auto' };
}
function resolvedClipId(choice: VoiceChoice): string | null {
  const id = choice.kind === 'clip' ? choice.id : defaultClipVoiceId;
  return id && hasClipVoice(id) ? id : null;
}

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const { status, user, updateFirstName } = useAuth();
  const [value, setValue] = useState<string>(() => {
    const c = getVoiceChoice();
    // Fold any legacy system-voice pref back to Automatic.
    return c.kind === 'clip' && hasClipVoice(c.id) ? `clip:${c.id}` : '';
  });

  const preview = (choice: VoiceChoice) => {
    const clipId = resolvedClipId(choice);
    if (!clipId) return;
    primeAudio();
    try {
      void new Audio(`/audio/${clipId}/complete.mp3`).play().catch(() => {});
    } catch {
      /* best effort */
    }
  };

  const choose = (nextValue: string) => {
    setValue(nextValue);
    const choice = parseValue(nextValue);
    setVoiceChoice(choice);
    preview(choice);
  };

  const currentClipId = resolvedClipId(parseValue(value));
  const currentLabel = currentClipId ? clipVoiceLabel(currentClipId) : '—';
  const autoLabel = defaultClipVoiceId ? clipVoiceLabel(defaultClipVoiceId) : '—';

  // Display name. Accounts created before we asked for one land here to set it.
  const signedIn = status === 'authenticated' && user;
  const [name, setName] = useState(user?.firstName ?? '');
  const [nameState, setNameState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [nameError, setNameError] = useState<string | null>(null);
  useEffect(() => setName(user?.firstName ?? ''), [user?.firstName]);

  const saveName = async () => {
    if (name.trim() === (user?.firstName ?? '')) return;
    setNameState('saving');
    setNameError(null);
    try {
      await updateFirstName(name);
      setNameState('saved');
    } catch (e) {
      setNameState('error');
      setNameError(e instanceof Error ? e.message : 'Could not save your name.');
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 className="auth-title">Settings</h2>
        <p className="auth-lede">
          {signedIn
            ? 'Your name and the spoken voice for routine cues.'
            : 'Choose the spoken voice for routine cues.'}
        </p>

        {signedIn && (
          <div className="setting setting-name">
            <label className="setting-label" htmlFor="first-name">
              First name
            </label>
            <div className="name-row">
              <input
                id="first-name"
                className="voice-select"
                type="text"
                maxLength={40}
                placeholder="Sam"
                autoComplete="given-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameState('idle');
                }}
                onBlur={saveName}
              />
              <button
                type="button"
                className="btn sm"
                disabled={nameState === 'saving' || name.trim() === (user?.firstName ?? '')}
                onClick={saveName}
              >
                {nameState === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
            <p className="muted setting-hint">
              {nameState === 'saved'
                ? 'Saved — that\u2019s what the menu will greet you with.'
                : nameError ?? 'Shown in the account menu instead of your email address.'}
            </p>
          </div>
        )}

        {CLIP_VOICES.length === 0 ? (
          <p className="muted">No cue voices are available.</p>
        ) : (
          <div className="setting">
            <label className="setting-label" htmlFor="voice-select">
              Cue voice
            </label>
            <select
              id="voice-select"
              className="voice-select"
              value={value}
              onChange={(e) => choose(e.target.value)}
            >
              <option value="">Automatic — {autoLabel}</option>
              {CLIP_VOICES.map((v) => (
                <option key={v.id} value={`clip:${v.id}`}>
                  {v.label} · {v.accent}
                </option>
              ))}
            </select>
            <p className="muted setting-hint">
              Currently using <strong>{currentLabel}</strong>. Selecting a voice plays a short
              sample. More voices and accents are on the way.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
